<template>
  <div class="services">
    <mPageTitle
      :title="$t(page.title)"
      :description="$t(page.description)"
      :image="require('@/assets/images/webp/services.webp')"
    />
    <mDelivery id="livrare" />
    <mJeffBag id="sacosa-jeff" />
    <mServices id="servicii" />
    <mPrices id="preturi" />
  </div>
</template>
<script>
import mPageTitle from "../components/molecules/m-page-title.vue";
import mPrices from "../components/molecules/m-prices.vue";
import mDelivery from "../components/molecules/m-delivery.vue";
import mJeffBag from "../components/molecules/m-jeff-bag.vue";
import mServices from "../components/molecules/m-services.vue";

const BASE_URL = "https://jeffbrasov.ro";

export default {
  metaInfo() {
    const lang = this.$i18n.locale;
    const pageUrl = `${BASE_URL}/${lang}/servicii-preturi`;
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: this.$t("Acasă"),
          item: `${BASE_URL}/${lang}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: this.$t("Servicii și Prețuri"),
          item: pageUrl,
        },
      ],
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name:
            "Care este diferența dintre Pachetele de Servicii și Abonamente?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Pachetul de Servicii va fi completat o singură dată — toate articolele trebuie aduse în aceeași zi. Abonamentul poate fi completat timp de o lună, ori de câte ori dorești — poți aduce articolele gradual pe parcursul celor 30 de zile.",
          },
        },
        {
          "@type": "Question",
          name:
            "Ce tipuri de articole sunt incluse în abonamentele, pachetele sau sacoșa Mr Jeff?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Abonamentele, pachetele și sacoșa Mr Jeff cuprind doar articolele de zi cu zi, care nu necesită tratament special sau curățare uscată.",
          },
        },
        {
          "@type": "Question",
          name:
            "Cum se tarifează articolele care necesită tratament special sau curățare uscată?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Articolele care necesită tratament special sau curățare uscată se vor tarifa separat, la prețul indicat în lista noastră de prețuri, cu acordul tău.",
          },
        },
        {
          "@type": "Question",
          name:
            "Care sunt câteva exemple de articole care nu sunt incluse în abonamentele, pachetele sau sacoșa Mr Jeff?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Exemple de articole excluse: sacou, costum, haină blană / piele, cojoc, geacă, pantofi, pilotă, pătură, articole de ocazie care necesită tratament special (rochie, fustă, bluză, etc.).",
          },
        },
        {
          "@type": "Question",
          name:
            "Care este valabilitatea abonamentului, cum este utilizat și cum sunt taxate articolele suplimentare?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Abonamentul este valabil 30 de zile. Nu este obligatoriu să aduci toate articolele în aceeași zi — le poți aduce gradual, ori de câte ori dorești. Dacă ai depășit numărul de articole din abonamentul ales, surplusul va fi tarifat la prețul indicat la 'articol extra' sau 'sacoșă extra', în funcție de abonamentul ales.",
          },
        },
      ],
    };
    const serviceSchemas = [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Spălătorie",
        name: "Curățare și Spălare Haine Brașov",
        provider: { "@id": BASE_URL },
        areaServed: "Brașov",
        url: pageUrl,
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Călcătorie",
        name: "Călcare Haine Brașov",
        provider: { "@id": BASE_URL },
        areaServed: "Brașov",
        url: pageUrl,
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Curățătorie Chimică",
        name: "Îndepărtarea Petelor Brașov",
        provider: { "@id": BASE_URL },
        areaServed: "Brașov",
        url: pageUrl,
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Colectare și Livrare",
        name: "Ridicare și Livrare Rufe la Domiciliu Brașov",
        provider: { "@id": BASE_URL },
        areaServed: "Brașov",
        url: pageUrl,
      },
    ];
    return {
      title: this.$t("seo_title_services"),
      meta: [
        { name: "description", content: this.$t("meta_desc_services") },
        {
          property: "og:title",
          content: `${this.$t("seo_title_services")} | Mr Jeff Brașov`,
        },
        { property: "og:description", content: this.$t("meta_desc_services") },
        { property: "og:url", content: pageUrl },
        {
          name: "twitter:title",
          content: `${this.$t("seo_title_services")} | Mr Jeff Brașov`,
        },
        { name: "twitter:description", content: this.$t("meta_desc_services") },
      ],
      link: [
        { rel: "canonical", href: pageUrl },
        {
          rel: "alternate",
          hreflang: "ro",
          href: `${BASE_URL}/ro/servicii-preturi`,
        },
        {
          rel: "alternate",
          hreflang: "en",
          href: `${BASE_URL}/en/servicii-preturi`,
        },
        {
          rel: "alternate",
          hreflang: "x-default",
          href: `${BASE_URL}/ro/servicii-preturi`,
        },
      ],
      script: [
        { type: "application/ld+json", json: breadcrumbSchema },
        { type: "application/ld+json", json: faqSchema },
        ...serviceSchemas.map((s) => ({
          type: "application/ld+json",
          json: s,
        })),
      ],
    };
  },
  components: {
    mPageTitle,
    mPrices,
    mDelivery,
    mJeffBag,
    mServices,
  },
  data() {
    return {
      page: {
        title: "Servicii și Prețuri",
        description:
          "Cu servicii premium, tehnologie de ultimă oră și prețuri accesibile, transformăm modul în care îți întreții garderoba",
      },
    };
  },
  mounted() {
    if (!this.$route.hash) {
      window.scrollTo({ top: 0 });
    }

    this.$watch(
      "$route.hash",
      (hash) => {
        if (hash) {
          this.scrollToHash(hash);
        }
      },
      { immediate: true },
    );
  },
  methods: {
    scrollToHash(hash) {
      // Wait for the section (and any async molecules) to render before scrolling.
      this.$nextTick(() => {
        const target = document.querySelector(hash);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
  },
};
</script>
